import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, Video, Eye, EyeOff } from 'lucide-react';
import { videosAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const ManageVideos = () => {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    youtube_url: '',
    thumbnail_url: '',
    duration: '',
    is_active: true,
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await videosAPI.getAllAdmin();
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = (video = null) => {
    if (video) {
      setEditingVideo(video);
      setFormData({
        title: video.title,
        description: video.description,
        youtube_url: video.youtube_url,
        thumbnail_url: video.thumbnail_url || '',
        duration: video.duration || '',
        is_active: video.is_active,
      });
    } else {
      setEditingVideo(null);
      setFormData({
        title: '',
        description: '',
        youtube_url: '',
        thumbnail_url: '',
        duration: '',
        is_active: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingVideo) {
        await videosAPI.update(editingVideo.id, formData);
      } else {
        await videosAPI.create(formData);
      }
      setIsModalOpen(false);
      fetchVideos();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save video');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await videosAPI.delete(id);
      fetchVideos();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete video');
    }
  };

  const toggleActive = async (video) => {
    try {
      await videosAPI.update(video.id, { is_active: !video.is_active });
      fetchVideos();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update video');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Manage Videos</h1>
          <p className="text-gray-400">Add, edit, and delete training videos</p>
        </div>
        <Button onClick={() => openModal()} className="bg-purple-600 hover:bg-purple-700">
          <Plus size={18} className="mr-2" />
          Add Video
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((video) => (
            <div
              key={video.id}
              className={`bg-[#1a1a2e] rounded-xl overflow-hidden border transition-all ${
                video.is_active ? 'border-gray-800' : 'border-red-800/50 opacity-60'
              }`}
            >
              <div className="relative">
                <img
                  src={video.thumbnail_url || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=300&h=200&fit=crop'}
                  alt={video.title}
                  className="w-full h-40 object-cover"
                />
                <div className="absolute top-2 right-2">
                  <Badge className={video.is_active ? 'bg-green-600' : 'bg-red-600'}>
                    {video.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                {video.duration && (
                  <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-white text-xs">
                    {video.duration}
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-white font-semibold mb-2 line-clamp-1">{video.title}</h3>
                <p className="text-gray-400 text-sm line-clamp-2 mb-4">{video.description}</p>
                
                <div className="flex items-center justify-between">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleActive(video)}
                    className="text-gray-400 hover:text-white"
                  >
                    {video.is_active ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => openModal(video)}
                      className="text-gray-400 hover:text-white h-8 w-8"
                    >
                      <Edit2 size={16} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(video.id)}
                      className="text-gray-400 hover:text-red-500 h-8 w-8"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {videos.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Video size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No videos yet. Add your first video!</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-gray-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Video size={20} />
              {editingVideo ? 'Edit Video' : 'Add New Video'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-400">Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-[#12121a] border-gray-700 text-white"
                placeholder="Video title"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-[#12121a] border-gray-700 text-white min-h-[80px]"
                placeholder="Video description"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">YouTube URL</Label>
              <Input
                value={formData.youtube_url}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                className="bg-[#12121a] border-gray-700 text-white"
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Thumbnail URL (Optional)</Label>
                <Input
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                  className="bg-[#12121a] border-gray-700 text-white"
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Duration</Label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="bg-[#12121a] border-gray-700 text-white"
                  placeholder="12:34"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-gray-400">Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)} className="border-gray-700 text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
              <Save size={16} className="mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageVideos;
